import { useConfigStore } from "../Stores/Config";

/**
 * Capture-request strings (contract §11). They are project static
 * translations like every other widget string (dashboard group "Capture"), so
 * they are read through the config store's staticTranslations — with these
 * English defaults for projects/servers that don't ship a key yet.
 */
export const CAPTURE_STRING_DEFAULTS = {
  captureTakeScreenshot: "Screenshot",
  captureRecordScreen: "Record screen",
  captureUploadFile: "Upload a file",
  captureSkip: "Not now",
  captureSkipMessage: "I can't share that right now.",
  captureWaiting: "Waiting for your screenshot or recording",
  captureSent: "Sent",
  captureSkipped: "Skipped",
  captureExpired: "This request has expired.",
  captureCancelled: "This request was withdrawn.",
  captureUploading: "Uploading…",
  captureClaimedElsewhere: "You're already answering this on another device.",
  captureNotSupported:
    "Screen capture isn't available here. You can upload a file instead.",
  capturePermissionDenied:
    "Screen capture was blocked. You can upload a file instead.",
  captureFailed: "That didn't work. Please try again or upload a file.",
  captureBarScreenshotHint: "Go to the screen, then capture",
  captureBarCapture: "Capture",
  captureBarCancel: "Cancel",
  captureBarRecordHint: "Go to where it happens, then start",
  captureBarStart: "Start",
  captureBarStop: "Stop",
  captureBarRecording: "Recording",
  captureBarDragHint: "Drag to move",
  captureMicrophone: "Microphone",
  capturePreviewTitle: "Send this recording?",
  capturePreviewSend: "Send",
  capturePreviewRetake: "Retake",
  capturePreviewPlay: "Play",
  capturePreviewPause: "Pause",
  captureRecordingInterrupted: "Recording stopped because the page changed.",
  captureRecordAgain: "Record again",
  // The web SDK offers a page recording when desktop screen sharing is denied.
  captureRecordPage: "Record this page",
  captureRecordPageHint:
    "Screen sharing didn't start. You can record this page instead.",
  captureEditorTitle: "Mark what matters",
  captureEditorPen: "Draw",
  captureEditorArrow: "Arrow",
  captureEditorRect: "Box",
  captureEditorText: "Text",
  captureEditorBlur: "Blur",
  captureEditorCrop: "Crop",
  captureEditorUndo: "Undo",
  captureEditorSend: "Send",
  captureEditorRetake: "Retake",
  captureLogsShared: "Diagnostic data was shared with the support team.",
  captureChannelHint: "Reply with a screenshot or screen recording.",
  // Messenger-side additions that §11 does not list yet. Same mechanism: a
  // project can translate them once the dashboard ships the keys; until then
  // the English default shows.
  captureEditorRedo: "Redo",
  captureEditorDiscardQuestion: "Discard your markup?",
  captureEditorKeepEditing: "Keep editing",
  capturePageRecording: "Page recording",
  captureVideo: "Video",
  captureUploadTooLarge: "This file is too large. Files can be up to 100 MB.",
  // Capture UI v2 (card tiles, states and receipts). {limit}, {elapsed} and
  // {percent} are filled in by the card.
  captureScreenshotHint: "One image",
  // Kai intake: the screenshot a native app took when the chat opened.
  captureHostShotTitle: "Is this the screen?",
  captureHostShotHint: "Taken when you opened the chat. Tap it to mark it up.",
  captureHostShotSend: "Send it",
  captureHostShotRetake: "Take a new one",
  captureHostShotEdit: "Mark up this screenshot",
  captureRecordLimit: "Up to {limit}",
  captureWaitingTitle: "Waiting for your capture…",
  captureWaitingHint: "Use the bar at the bottom of your screen.",
  captureEditingTitle: "Marking up your screenshot",
  captureEditingHint: "Send it from the editor when you're ready.",
  captureReadyTitle: "Ready to record",
  captureReadyHint: "Start from the bar at the bottom of your screen.",
  captureRecordingPaused: "Recording paused",
  captureRecordingOf: "{elapsed} of {limit}",
  captureReviewTitle: "Review your recording",
  captureReviewHint: "Send it or retake it from the preview.",
  captureSendingTitle: "Sending…",
  captureUploadingPercent: "Uploading… {percent}%",
  captureSentMarkedUp: "Marked up",
  captureSentAsCaptured: "As captured",
  captureSentWithAudio: "With audio",
  captureSentNoAudio: "No audio",
  captureJustNow: "Just now",
  captureScreenshotLabel: "Screenshot",
  captureRecordingLabel: "Recording",
  captureSkippedDetail: "no capture shared",
  captureShareOne: "Share one",
  captureEditorHint: "Draw on the screenshot, or send it as it is.",
  captureEditorColor: "Colour",
  // Host labels new in capture UI v2 ({enter} / {esc} become key caps).
  captureBarCaptureKeys: "{enter} to capture · {esc} to cancel",
  captureBarStartKeys: "{enter} to start · {esc} to cancel",
  captureBarPause: "Pause",
  captureBarResume: "Resume",
  captureBarPaused: "Paused",
  capturePreviewDiscard: "Discard",
  capturePreviewWithAudio: "with audio",
  capturePreviewNoAudio: "no audio",
};

export type CaptureStringKey = keyof typeof CAPTURE_STRING_DEFAULTS;

/** Fills `{name}` placeholders. */
export const fillCaptureText = (
  text: string,
  values: Record<string, string | number>,
): string =>
  text.replace(/\{(\w+)\}/g, (match, name) =>
    values[name] !== undefined ? String(values[name]) : match,
  );

export const captureText = (key: CaptureStringKey): string =>
  useConfigStore
    .getState()
    .translateTextWithFallback(key, CAPTURE_STRING_DEFAULTS[key]);

/**
 * Hook form of captureText: re-renders when the project's translations change
 * (config-update) and returns a lookup bound to them.
 */
export const useCaptureText = () => {
  const staticTranslations = useConfigStore(
    (state) => state.config?.staticTranslations,
  );
  return (key: CaptureStringKey): string => {
    const value = staticTranslations?.[key];
    return typeof value === "string" && value.length > 0
      ? value
      : CAPTURE_STRING_DEFAULTS[key];
  };
};

// The subset hosts render in their own capture UI (bar, preview, errors). The
// Messenger hands them over in `capture-start.labels`, keyed without the
// `capture` prefix in lower camel case (captureBarCapture → barCapture), so no
// SDK needs a translation table of its own.
const HOST_LABEL_KEYS: CaptureStringKey[] = [
  "captureBarScreenshotHint",
  "captureBarCapture",
  "captureBarCancel",
  "captureBarRecordHint",
  "captureBarStart",
  "captureBarStop",
  "captureBarRecording",
  "captureBarDragHint",
  "captureMicrophone",
  "capturePreviewTitle",
  "capturePreviewSend",
  "capturePreviewRetake",
  "capturePreviewPlay",
  "capturePreviewPause",
  "captureUploading",
  "captureRecordingInterrupted",
  "captureRecordAgain",
  "captureRecordPage",
  "captureRecordPageHint",
  "capturePermissionDenied",
  "captureNotSupported",
  "captureFailed",
  "captureBarCaptureKeys",
  "captureBarStartKeys",
  "captureBarPause",
  "captureBarResume",
  "captureBarPaused",
  "capturePreviewDiscard",
  "capturePreviewWithAudio",
  "capturePreviewNoAudio",
];

export const hostLabelName = (key: CaptureStringKey): string => {
  const withoutPrefix = key.replace(/^capture/, "");
  return withoutPrefix.charAt(0).toLowerCase() + withoutPrefix.slice(1);
};

export const buildCaptureHostLabels = (): Record<string, string> => {
  const labels: Record<string, string> = {};
  HOST_LABEL_KEYS.forEach((key) => {
    labels[hostLabelName(key)] = captureText(key);
  });
  return labels;
};
